import { z } from 'zod';

// ─── Login form validation schema ────────────────────────────────────────────

export const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters'),
});

export type LoginFormValues = z.infer<typeof loginSchema>;

// ─── Magic-link request schema (no password) ─────────────────────────────────

export const magicLinkSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Enter a valid email address'),
});

export type MagicLinkFormValues = z.infer<typeof magicLinkSchema>;

// ─── Supabase auth error → user-friendly message ─────────────────────────────

/**
 * Maps raw Supabase auth error messages to user-facing strings.
 * Never exposes internal Supabase error text to end users.
 */
export function normaliseAuthError(message: string): string {
  const lower = message.toLowerCase();

  if (lower.includes('invalid login') || lower.includes('invalid credentials')) {
    return 'Invalid email or password. Please try again.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Your email is not confirmed. Contact your administrator.';
  }
  if (lower.includes('too many requests') || lower.includes('rate limit')) {
    return 'Too many sign-in attempts. Please wait a moment and try again.';
  }
  if (
    lower.includes('network') ||
    lower.includes('fetch') ||
    lower.includes('failed to fetch')
  ) {
    return 'Unable to connect. Please check your internet connection and try again.';
  }

  // Generic fallback.
  return 'Unable to sign in. Please try again or contact your administrator.';
}
